import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import "./Login.css";

export default function LoginPage() {
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const returnTo = useMemo(
    () => location.state?.from?.pathname || "/",
    [location.state],
  );

  if (authLoading) {
    return (
      <div className="auth-loading">
        <div className="login-loader">
          <div className="login-loader-ring" />
          <span>Loading secure portal...</span>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!identifier.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await login(identifier, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Login failed. Check your credentials and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Animated background */}
      <div className="auth-background">
        <div className="auth-orb auth-orb-one" />
        <div className="auth-orb auth-orb-two" />
        <div className="auth-grid" />
      </div>

      <div className="auth-shell">
        {/* LEFT BRAND PANEL */}
        <section className="auth-brand-panel">
          <div>
            <div className="auth-brand-top">
              <div className="auth-logo">
                <span>AHC</span>
              </div>

              <div className="auth-brand-chip">Mulu Health PLC</div>
            </div>

            <div className="auth-brand-content">
              <div className="auth-eyebrow">
                <span className="status-dot" />
                PHC Capacity Assessment
              </div>

              <h1>
                Assess.
                <br />
                <span>Strengthen. Transform.</span>
              </h1>

              <p>
                A structured platform for assessing primary healthcare capacity,
                documenting evidence, reviewing performance, and generating
                professional reports.
              </p>
            </div>

            <div className="auth-features">
              <div className="auth-feature">
                <div className="auth-feature-icon">✓</div>
                <div>
                  <strong>Structured assessments</strong>
                  <span>Evaluate every PHC indicator systematically.</span>
                </div>
              </div>

              <div className="auth-feature">
                <div className="auth-feature-icon">⌁</div>
                <div>
                  <strong>Evidence-based documentation</strong>
                  <span>Attach supporting evidence to every indicator.</span>
                </div>
              </div>

              <div className="auth-feature">
                <div className="auth-feature-icon">↗</div>
                <div>
                  <strong>Professional reporting</strong>
                  <span>Generate complete assessment reports.</span>
                </div>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            <span>AHC PHC Assessment Portal</span>
            <span className="auth-version">v5.0</span>
          </div>
        </section>

        {/* LOGIN CARD */}
        <section className="auth-card">
          <div className="auth-card-header">
            <div>
              <div className="auth-card-kicker">Secure access</div>

              <h2>Welcome back</h2>

              <p>Sign in to continue to your assessment workspace.</p>
            </div>

            <div className="auth-card-mark">
              <span>↗</span>
            </div>
          </div>

          <form
            className="auth-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <label className="auth-field">
              <span>Username or email</span>

              <div className="auth-input-wrapper">
                <span className="auth-input-icon">◎</span>

                <input
                  name="login-identifier"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="auth-input"
                  placeholder="Enter your username"
                  autoComplete="off"
                  disabled={loading}
                />
              </div>
            </label>

            <label className="auth-field">
              <span>Password</span>

              <div className="auth-input-wrapper">
                <span className="auth-input-icon">⌑</span>

                <input
                  name="login-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="auth-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  autoComplete="new-password"
                  disabled={loading}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((current) => !current)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                      <circle cx="12" cy="12" r="2.5" />
                    </svg>
                  ) : (
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m3 3 18 18" />
                      <path d="M10.6 5.1A10.8 10.8 0 0 1 12 5c6 0 9.5 7 9.5 7a18.6 18.6 0 0 1-3.1 3.9" />
                      <path d="M6.7 6.7C3.9 8.4 2.5 12 2.5 12s3.5 6 9.5 6c1.4 0 2.7-.3 3.8-.8" />
                    </svg>
                  )}
                </button>
              </div>
            </label>

            {error && (
              <div className="auth-error" role="alert">
                <div className="auth-error-icon">!</div>

                <div>
                  <strong>Unable to sign in</strong>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <span className="auth-submit-arrow">→</span>
                </>
              )}
            </button>
          </form>

          <div className="auth-divider">
            <span />
            <small>Authorized users only</small>
            <span />
          </div>

          <div className="auth-access-note">
            <div className="auth-access-icon">i</div>

            <div>
              <strong>Need access?</strong>
              <span>
                If you do not have an account, contact your system
                administrator.
              </span>
            </div>
          </div>

          <div className="auth-security">
            <span className="security-dot" />
            Secure role-based access
          </div>
        </section>
      </div>
    </div>
  );
}
